// ============================================================
// dropboxImporter.ts
// ------------------------------------------------------------
// Importerar förparsade kvitto-poster från scripts/firestore-input-*.json
// in i Firestore på path:  users/{uid}/companies/{companyId}/receipts/{auto}
//
// Skapas av PowerShell-skriptet scripts/scan-dropbox-to-firestore-input.ps1
// och körs MANUELLT från dev-konsol (vi pushar inte automatiskt).
//
// JSON-format (per post):
//   {
//     companyId: 'occdec' | 'linesp-ab',
//     companyName: string,
//     category: 'invoice_in' | 'invoice_out' | 'expense',
//     filename: string,
//     date: 'YYYY-MM-DD' | null,
//     vendor: string | null,
//     amount: number | null,
//     currency: string | null,
//     status: 'parsed' | 'soft_parsed' | 'needs_review',
//     sourcePath: string,    // lokal Windows-path, ENDAST för debug
//     folderRel: string,
//     sizeBytes: number
//   }
//
// TODO: lägg till storage-upload av själva PDF-filen från sourcePath
//       (idag bara metadata — Firebase Storage-upload kräver att filen
//       läses upp via input-element i UI eller via Node-script med
//       firebase-admin SDK).
// ============================================================

import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { db } from '../firebase'
import { recordSync } from './cloudStore'
import type { ReceiptCategory } from './receipts'

export interface DropboxScanPost {
  companyId: string
  companyName: string
  category: 'invoice_in' | 'invoice_out' | 'expense'
  filename: string
  date: string | null
  vendor: string | null
  amount: number | null
  currency: string | null
  status: 'parsed' | 'soft_parsed' | 'needs_review'
  sourcePath: string
  folderRel: string
  sizeBytes: number
}

export interface ImportResult {
  ok: number
  skipped: number
  failed: number
  errors: Array<{ filename: string; error: string }>
}

/**
 * Importera en JSON-array (laddad i klienten via fetch eller fileInput) till Firestore.
 * Anropa från dev-konsol — vi vill INTE auto-pusha.
 *
 * Exempel:
 *   const text = await fetch('/firestore-input-2026-05-20.json').then(r => r.text())
 *   const posts = JSON.parse(text) as DropboxScanPost[]
 *   await importPosts(posts, auth.currentUser!.uid)
 */
/** Mappa intern Dropbox-scanner-kategori → ReceiptCategory (anvnds både i Firestore-doc och recordSync).
 *
 * SEMANTIK (användarens egen konvention på pärmstrukturen):
 *   - "2026 LS Faktura IN"  = kundfakturor (intäkter) — pengar IN på kontot
 *   - "2026 LS Faktura UT"  = leverantörsfakturor (kostnader) — vi har betalt UT
 *   - "2026 LS Utlägg"      = utlägg (kostnader)
 */
function mapCategory(c: DropboxScanPost['category']): ReceiptCategory {
  if (c === 'invoice_in')  return 'kundfakturor'         // Faktura IN = intäkt
  if (c === 'invoice_out') return 'leverantorsfakturor'  // Faktura UT = kostnad
  return 'utlagg'                                        // Utlägg = kostnad
}

/** Plocka år+månad ur YYYY-MM-DD-sträng. Fallback till nuvarande datum om ogiltigt. */
function extractYearMonth(date: string | null): { year: number; month: number } {
  if (date && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    const y = parseInt(date.slice(0, 4), 10)
    const m = parseInt(date.slice(5, 7), 10) - 1  // 0-indexerat
    if (!isNaN(y) && !isNaN(m) && m >= 0 && m <= 11) return { year: y, month: m }
  }
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() }
}

export async function importPosts(
  posts: DropboxScanPost[],
  uid: string,
  opts: { dryRun?: boolean; skipNeedsReview?: boolean } = {},
): Promise<ImportResult> {
  const result: ImportResult = { ok: 0, skipped: 0, failed: 0, errors: [] }
  const { dryRun = false, skipNeedsReview = false } = opts

  // Feature 1: rkna upp per (companyId, year, category) sa vi kan
  // anropa recordSync() en gang per kombination i slutet.
  const syncCounts = new Map<string, { companyId: string; year: number; category: ReceiptCategory; count: number }>()

  // Idempotens-cache: per-companyId-set med befintliga (filename|date|amount)-tupler
  // hämtas en gång per körning för att undvika dubbletter när importen körs flera gånger.
  const existingKeys = new Map<string, Set<string>>()
  async function loadExistingKeys(companyId: string): Promise<Set<string>> {
    const cached = existingKeys.get(companyId)
    if (cached) return cached
    const set = new Set<string>()
    try {
      const colRef = collection(db, 'users', uid, 'companies', companyId, 'receipts')
      // Hämta bara dropbox-scanner-poster — färre att läsa
      const q = query(colRef, where('source', '==', 'dropbox-scanner'))
      const snap = await getDocs(q)
      snap.forEach((d) => {
        const x = d.data() as { filename?: string; date?: string | null; amount?: number | null }
        const k = (x.filename ?? '') + '|' + (x.date ?? '') + '|' + (x.amount ?? '')
        set.add(k)
      })
    } catch {
      // Om vi inte kan läsa — fortsätt utan idempotens (mindre säkert men inte fatalt)
    }
    existingKeys.set(companyId, set)
    return set
  }

  for (const p of posts) {
    if (skipNeedsReview && p.status === 'needs_review') {
      result.skipped++
      continue
    }
    if (!p.companyId || !p.category || !p.filename) {
      result.skipped++
      continue
    }

    // FIX (2026-05-20): mappa rå scanner-kategori → ReceiptCategory så aggregateReceipts ser den
    const mappedCategory: ReceiptCategory = mapCategory(p.category)
    // FIX (2026-05-20): plocka year+month ur date-fältet så subscribeReceipts where('year','==',year) träffar
    const { year: y, month: m } = extractYearMonth(p.date)

    // Idempotens-check: hoppa över om filename+date+amount redan finns för bolaget
    if (!dryRun) {
      const keys = await loadExistingKeys(p.companyId)
      const dupKey = p.filename + '|' + (p.date ?? '') + '|' + (p.amount ?? '')
      if (keys.has(dupKey)) {
        result.skipped++
        continue
      }
      keys.add(dupKey)  // markera direkt så samma post i samma run inte importeras dubbelt
    }

    // Skiss på Firestore-doc (jämför med CloudReceipt i cloudStore.ts):
    //   { filename, date, category:'kundfakturor', amount, currency, vendor,
    //     year:2026, month:4, storagePath:'', status:'soft_parsed',
    //     source:'dropbox-scanner', sourcePath, folderRel, sizeBytes, createdAt }
    // currency: explicit + warn (2026-06-02 subagent, per review "make warn + explicit" to avoid tyst SEK bug).
    // Bakåtkompat default SEK, men alltid log + p.currency || 'SEK' (bättre än ?? för falsy).
    // Rek: i ReceiptsCard/aggs visa varning "X poster i utländsk valuta exkluderade från SEK-summor".
    if (!p.currency) {
      console.warn('[dropboxImporter] currency saknas (explicit SEK default per review):', p.filename, { sourcePath: p.sourcePath })
    }
    const doc = {
      filename:  p.filename,
      date:      p.date ?? null,
      category:  mappedCategory,          // FIX: mappad enum-värde, inte rå scanner-sträng
      amount:    p.amount ?? null,
      currency:  p.currency || 'SEK',
      vendor:    p.vendor ?? null,
      year:      y,                       // FIX: krävs av subscribeReceipts where('year','==',year)
      month:     m,                       // FIX: krävs av aggregateReceipts (months[r.month])
      // storagePath lämnas tom här — fyll i när PDF är uppladdad till Storage
      storagePath: '',
      status:    p.status === 'parsed' ? 'parsed' : 'needs_review',
      source:    'dropbox-scanner',
      // Debug-fält så vi kan spåra tillbaka till lokal fil
      sourcePath: p.sourcePath,
      folderRel:  p.folderRel,
      sizeBytes:  p.sizeBytes,
      createdAt:  serverTimestamp(),
    }

    if (dryRun) {
      result.ok++
      continue
    }

    try {
      const colRef = collection(
        db,
        'users', uid, 'companies', p.companyId, 'receipts',
      )
      await addDoc(colRef, doc)
      result.ok++

      // Feature 1: rkna upp sync-count fr denna (companyId, year, category)
      const key = p.companyId + '|' + y + '|' + mappedCategory
      const cur = syncCounts.get(key) ?? { companyId: p.companyId, year: y, category: mappedCategory, count: 0 }
      cur.count += 1
      syncCounts.set(key, cur)
    } catch (err: unknown) {
      result.failed++
      result.errors.push({
        filename: p.filename,
        error:    err instanceof Error ? err.message : String(err),
      })
    }
  }

  // Feature 1: skriv en recordSync per (companyId, year, category) i en svep.
  // Vi anvnder dropbox-scanner-source.
  if (!dryRun) {
    for (const { companyId, year, category, count } of syncCounts.values()) {
      try {
        await recordSync(companyId, year, category, count, 'dropbox-scanner')
      } catch (err) {
        console.warn('recordSync (dropbox) failed for', companyId, year, category, err)
      }
    }
  }

  return result
}

/**
 * Wrapper som läser JSON från en fetch-bar URL (placera filen i public/ vid behov).
 * VARNING: pusha inte utan användarens uttryckliga OK.
 */
export async function importFromJson(jsonPath: string, uid: string): Promise<ImportResult> {
  const res = await fetch(jsonPath)
  if (!res.ok) throw new Error(`Failed to fetch JSON: ${res.status} ${res.statusText}`)
  const posts = (await res.json()) as DropboxScanPost[]
  return importPosts(posts, uid)
}
